import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Award, CalendarDays, Clock, Users, MapPin, Loader2, Search, Coins } from "lucide-react";
import { motion } from "framer-motion";
import SportAutocomplete from "@/components/SportAutocomplete";
import { getSportColor } from "@/lib/sports";

export default function BrowseSessions() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sport, setSport] = useState("");
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const params = {};
      if (sport && sport !== "all") params.sport = sport;
      if (query) params.q = query;
      const res = await api.get("/sessions/browse", { params });
      setSessions(res.data);
    } catch { setSessions([]); }
    setLoading(false);
  };

  useEffect(() => { fetchSessions(); }, []); // eslint-disable-line

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-10">
      <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight flex items-center gap-3 mb-1" data-testid="sessions-page-title">
        <Award className="w-8 h-8 text-court-orange" />Training Sessions
      </h1>
      <p className="text-muted-foreground mb-6">Join coach-led sessions and pickup games</p>

      <div className="flex flex-col md:flex-row gap-3 mb-8 p-4 bg-white border-2 border-slate-200" data-testid="session-filters">
        <div className="flex-1">
          <Input data-testid="session-search" value={query} onChange={e => setQuery(e.target.value)}
            placeholder="Search sessions..." className="h-11 rounded-none border-2" onKeyDown={e => e.key === "Enter" && fetchSessions()} />
        </div>
        <SportAutocomplete value={sport} onValueChange={setSport} placeholder="All Sports" triggerClassName="w-full md:w-52" />
        <Button data-testid="session-search-btn" onClick={fetchSessions}
          className="bg-court-orange hover:bg-court-orange-hover text-white rounded-full font-heading uppercase px-8 h-11 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]">
          <Search className="w-4 h-4 mr-2" />Search
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>
      ) : sessions.length === 0 ? (
        <div className="text-center py-20 border-2 border-dashed border-slate-200">
          <Award className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="font-heading text-xl text-muted-foreground uppercase">No sessions available</p>
          <p className="text-sm text-muted-foreground mt-2">Check back soon for new training sessions</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="sessions-grid">
          {sessions.map((s, i) => {
            const isFree = !s.price_amount || s.price_amount <= 0;
            return (
              <motion.div key={s.session_id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }} onClick={() => navigate(`/coach/sessions/${s.session_id}`)}
                className="bg-white border-b-4 border-r-4 border-black p-5 cursor-pointer hover:-translate-y-1 transition-transform duration-200"
                data-testid={`session-card-${s.session_id}`}>
                <div className="flex items-center gap-2 mb-3">
                  <Badge className={`${getSportColor(s.sport)} text-[10px] font-heading uppercase tracking-wider`}>{s.sport}</Badge>
                  {isFree && <Badge className="bg-lime-volt text-black text-[10px] font-heading uppercase">Free</Badge>}
                  {s.spots_left <= 3 && s.spots_left > 0 && <Badge className="bg-red-500 text-white text-[10px] font-heading uppercase">{s.spots_left} left</Badge>}
                </div>
                <h3 className="font-heading text-base font-bold uppercase tracking-tight mb-2">{s.title}</h3>
                <div className="space-y-1.5 text-sm text-muted-foreground">
                  <p className="flex items-center gap-1.5"><Award className="w-3.5 h-3.5 text-court-orange" />Coach {s.host_name}</p>
                  <p className="flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" />{s.session_date}</p>
                  <p className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />{s.start_time} ({s.duration_minutes}min)</p>
                  {s.venue_name && <p className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{s.venue_name}</p>}
                  <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                    <span className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5" />{s.participants_count}/{s.slot_limit}</span>
                    <span className="font-heading text-lg font-bold text-court-orange">
                      {isFree ? "FREE" : `₱${s.price_amount?.toFixed(0)}`}
                    </span>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
